"use client";

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { AlertCircle, FileText } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TermsOfUseOutput } from "@/interfaces/terms-of-use";
import { TermsOfUseService } from "@/services/TermsOfUseService";

const TermsOfUse: React.FC = () => {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsData, setTermsData] = useState<TermsOfUseOutput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const navigate = useNavigate();
  const termsService = new TermsOfUseService();

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoading(true);
        const [terms] = (await termsService.findAll({ filter: "LATEST" })).data;
        setTermsData(terms);

        // Fetch the PDF as a blob and create an object URL to prevent download
        if (terms.url) {
          try {
            const response = await fetch(terms.url, { mode: "no-cors" });
            if (!response.ok) throw new Error("Erro ao buscar termos de uso");
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
          } catch {
            setPdfUrl(terms.url);
          }
        } else {
          throw new Error("Erro ao buscar termos de uso");
        }

        setError(null);
      } catch (err) {
        console.error("Failed to fetch terms of use:", err);
        setError(
          "Não foi possível carregar os termos de uso. Por favor, tente novamente mais tarde."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTerms();

    // Clean up any created object URLs when component unmounts
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, []);

  const handleAccept = async () => {
    if (!accepted || !termsData) return;

    setIsSubmitting(true);
    try {
      // Save the user's acceptance to your backend
      await termsService.accept(termsData.id);
      navigate("/");
      window.location.reload();
    } catch {
      setError(
        "Não foi possível salvar sua aceitação. Por favor, tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Termos de Uso</CardTitle>
          {termsData && (
            <div className="text-sm text-muted-foreground">
              Versão {termsData.version} • Atualizado em{" "}
              {new Date(termsData.updatedAt).toLocaleDateString()}
            </div>
          )}
        </CardHeader>
        <CardContent className="h-[60vh] overflow-hidden">
          {error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Erro</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : pdfUrl ? (
            <div className="h-full w-full">
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                className="h-full w-full rounded-md border border-border"
                title="Termos de Uso"
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">
                  Nenhum documento de termos disponível.
                </p>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked as boolean)}
              disabled={!pdfUrl || !!error}
            />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Li e aceito os termos de uso
            </label>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="w-full sm:w-auto"
            >
              Recusar
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!accepted || isSubmitting || !pdfUrl || !!error}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? "Processando..." : "Aceitar e Continuar"}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default TermsOfUse;
